/**
 * FIREBASE TABLE SERVICE - Complete Table Management
 * Handles all table operations in Firebase Firestore
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  onSnapshot,
  serverTimestamp
} from '../services/firestoreCompat';
import { db } from './firebaseConfig';

// Collection name
const TABLES_COLLECTION = 'restaurant_tables';

// Table interface
export interface FirebaseTable {
  id?: string;
  number: number;
  status: 'available' | 'occupied' | 'reserved' | 'cleaning';
  capacity: number;
  area: 'indoor' | 'outdoor';
  customer_name?: string;
  total_amount?: number;
  item_count?: number;
  start_time?: string;
  end_time?: string;
  order_id?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  branch?: string;
}

class FirebaseTableService {
  private static instance: FirebaseTableService;

  public static getInstance(): FirebaseTableService {
    if (!FirebaseTableService.instance) {
      FirebaseTableService.instance = new FirebaseTableService();
    }
    return FirebaseTableService.instance;
  }

  /**
   * Create a new table
   */
  public async createTable(tableData: Omit<FirebaseTable, 'id' | 'created_at' | 'updated_at'>): Promise<FirebaseTable> {
    try {
      console.log('🪑 [FIREBASE TABLE] Creating table:', tableData);
      
      const firebaseTableData = {
        ...tableData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'Admin Panel',
        branch: 'saheeh-riyadh'
      };

      const docRef = await addDoc(collection(db, TABLES_COLLECTION), {
        ...firebaseTableData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const createdTable: FirebaseTable = {
        ...firebaseTableData,
        id: docRef.id
      };

      console.log('✅ [FIREBASE TABLE] Table created successfully:', createdTable.id);
      return createdTable;

    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to create table:', error);
      throw error;
    }
  }

  /**
   * Get all tables
   */
  public async getTables(): Promise<FirebaseTable[]> {
    try {
      console.log('🪑 [FIREBASE TABLE] Fetching all tables...');
      
      const q = query(collection(db, TABLES_COLLECTION), orderBy('number', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const tables: FirebaseTable[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        tables.push({
          id: doc.id,
          number: data.number,
          status: data.status,
          capacity: data.capacity,
          area: data.area,
          customer_name: data.customer_name,
          total_amount: data.total_amount,
          item_count: data.item_count,
          start_time: data.start_time,
          end_time: data.end_time,
          order_id: data.order_id,
          created_at: data.created_at,
          updated_at: data.updated_at,
          created_by: data.created_by,
          branch: data.branch
        } as FirebaseTable);
      });
      
      console.log(`✅ [FIREBASE TABLE] Loaded ${tables.length} tables from Firebase`);
      return tables;
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to fetch tables:', error);
      throw error;
    }
  }

  /**
   * Update table properties (number, capacity, area, etc.)
   */
  public async updateTable(
    tableId: string,
    updates: {
      number?: number;
      capacity?: number;
      area?: 'indoor' | 'outdoor';
      status?: FirebaseTable['status'];
    }
  ): Promise<void> {
    try {
      console.log('🪑 [FIREBASE TABLE] Updating table:', { tableId, updates });
      
      const tableRef = doc(db, TABLES_COLLECTION, tableId);
      const updateData: any = {
        updated_at: new Date().toISOString(),
        updatedAt: serverTimestamp()
      };
      
      if (updates.number !== undefined) updateData.number = updates.number;
      if (updates.capacity !== undefined) updateData.capacity = updates.capacity;
      if (updates.area !== undefined) updateData.area = updates.area;
      if (updates.status !== undefined) updateData.status = updates.status;
      
      await updateDoc(tableRef, updateData);
      console.log('✅ [FIREBASE TABLE] Table updated successfully');
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to update table:', error);
      throw error;
    }
  }

  /**
   * Update table status
   */
  public async updateTableStatus(
    tableId: string, 
    status: FirebaseTable['status'], 
    customerData?: {
      customer_name?: string | null;
      total_amount?: number | null;
      item_count?: number | null;
      start_time?: string | null;
      end_time?: string | null;
      order_id?: string | null;
    }
  ): Promise<void> {
    try {
      console.log('🪑 [FIREBASE TABLE] Updating table status:', { tableId, status, customerData });
      
      const { deleteField } = await import('../services/firestoreCompat');
      const tableRef = doc(db, TABLES_COLLECTION, tableId);
      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
        updatedAt: serverTimestamp()
      };
      
      if (customerData) {
        // Explicitly set fields to deleteField() if they're null/undefined to clear them
        if (customerData.customer_name === null || customerData.customer_name === undefined) {
          updateData.customer_name = deleteField();
        } else {
          updateData.customer_name = customerData.customer_name;
        }
        
        if (customerData.total_amount === null || customerData.total_amount === undefined) {
          updateData.total_amount = deleteField();
        } else {
          updateData.total_amount = customerData.total_amount;
        }
        
        if (customerData.item_count === null || customerData.item_count === undefined) {
          updateData.item_count = deleteField();
        } else {
          updateData.item_count = customerData.item_count;
        }
        
        if (customerData.start_time === null || customerData.start_time === undefined) {
          updateData.start_time = deleteField();
        } else {
          updateData.start_time = customerData.start_time;
        }
        
        if (customerData.end_time === null || customerData.end_time === undefined) {
          updateData.end_time = deleteField();
        } else {
          updateData.end_time = customerData.end_time;
        }
        
        if (customerData.order_id === null || customerData.order_id === undefined) {
          updateData.order_id = deleteField();
        } else {
          updateData.order_id = customerData.order_id;
        }
      }
      
      await updateDoc(tableRef, updateData);
      console.log('✅ [FIREBASE TABLE] Table status updated successfully:', updateData);
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to update table status:', error);
      throw error;
    }
  }

  /**
   * Update table by number (alternative method)
   * CRITICAL: Now requires area to prevent updating wrong table when duplicate numbers exist
   */
  public async updateTableByNumber(
    tableNumber: number, 
    status: FirebaseTable['status'], 
    customerData?: {
      customer_name?: string;
      total_amount?: number;
      item_count?: number;
      start_time?: string;
      end_time?: string;
      order_id?: string;
    },
    area?: 'indoor' | 'outdoor' // CRITICAL: Area is now required to prevent wrong table updates
  ): Promise<void> {
    try {
      console.log('🪑 [FIREBASE TABLE] Updating table by number:', { tableNumber, area, status, customerData });
      
      // CRITICAL: Filter by both number AND area to ensure we update the correct table
      // This prevents updating the wrong table when duplicate numbers exist across areas
      let q;
      if (area) {
        q = query(
          collection(db, TABLES_COLLECTION), 
          where('number', '==', tableNumber),
          where('area', '==', area)
        );
      } else {
        // Fallback: If area not provided, query by number only (for backward compatibility)
        // But warn about potential issues
        console.warn(`⚠️ [FIREBASE TABLE] updateTableByNumber called without area for table ${tableNumber} - this may update wrong table if duplicates exist`);
        q = query(collection(db, TABLES_COLLECTION), where('number', '==', tableNumber));
      }
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error(`Table ${tableNumber}${area ? ` (${area})` : ''} not found`);
      }
      
      // If multiple tables found (shouldn't happen with area filter, but handle it)
      if (querySnapshot.docs.length > 1) {
        console.warn(`⚠️ [FIREBASE TABLE] Multiple tables found for number ${tableNumber}${area ? ` in ${area}` : ''} - updating first one`);
      }
      
      const tableDoc = querySnapshot.docs[0];
      const tableId = tableDoc.id;
      const tableData = tableDoc.data();
      
      // Verify the area matches if provided
      if (area && tableData.area !== area) {
        throw new Error(`Table ${tableNumber} found but area mismatch: expected ${area}, found ${tableData.area}`);
      }
      
      // Update the table
      await this.updateTableStatus(tableId, status, customerData);
      
      console.log(`✅ [FIREBASE TABLE] Updated table ${tableNumber}${area ? ` (${area})` : ''} successfully`);
      
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to update table by number:', error);
      throw error;
    }
  }

  /**
   * Delete a table
   */
  public async deleteTable(tableId: string): Promise<void> {
    try {
      console.log('🪑 [FIREBASE TABLE] Deleting table:', tableId);
      
      await deleteDoc(doc(db, TABLES_COLLECTION, tableId));
      console.log('✅ [FIREBASE TABLE] Table deleted successfully');
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to delete table:', error);
      throw error;
    }
  }

  /**
   * Delete all tables from Firebase
   * WARNING: This will permanently delete all tables!
   */
  public async deleteAllTables(): Promise<number> {
    try {
      console.log('🗑️ [FIREBASE TABLE] Deleting all tables...');
      
      const allTables = await this.getTables();
      let deletedCount = 0;
      
      for (const table of allTables) {
        if (table.id) {
          try {
            await this.deleteTable(table.id);
            deletedCount++;
            console.log(`✅ [FIREBASE TABLE] Deleted table ${table.number} (${table.area})`);
          } catch (error) {
            console.error(`❌ [FIREBASE TABLE] Failed to delete table ${table.number}:`, error);
          }
        }
      }
      
      console.log(`✅ [FIREBASE TABLE] Deleted ${deletedCount} out of ${allTables.length} tables`);
      return deletedCount;
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to delete all tables:', error);
      throw error;
    }
  }

  /**
   * Listen to table changes in real-time
   */
  public onTablesChange(callback: (tables: FirebaseTable[]) => void): () => void {
    console.log('🪑 [FIREBASE TABLE] Setting up real-time listener...');
    
    const q = query(collection(db, TABLES_COLLECTION), orderBy('number', 'asc'));
    
    return onSnapshot(q, 
      (querySnapshot) => {
        const tables: FirebaseTable[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          tables.push({
            id: doc.id,
            number: data.number,
            status: data.status,
            capacity: data.capacity,
            area: data.area,
            customer_name: data.customer_name,
            total_amount: data.total_amount,
            item_count: data.item_count,
            start_time: data.start_time,
            end_time: data.end_time,
            order_id: data.order_id,
            created_at: data.created_at,
            updated_at: data.updated_at,
            created_by: data.created_by,
            branch: data.branch
          } as FirebaseTable);
        });
        
        console.log('🔄 [FIREBASE TABLE] Tables updated from real-time listener:', tables.length);
        callback(tables);
      },
      (error) => {
        console.error('❌ [FIREBASE TABLE] Real-time listener error:', error);
        callback([]);
      }
    );
  }

  /**
   * Reset a table - clear all order/customer data and set status to available
   */
  public async resetTable(tableId: string): Promise<void> {
    try {
      console.log('🔄 [FIREBASE TABLE] Resetting table:', tableId);

      const { deleteField } = await import('../services/firestoreCompat');
      const tableRef = doc(db, TABLES_COLLECTION, tableId);

      await updateDoc(tableRef, {
        status: 'available',
        customer_name: deleteField(),
        customer_phone: deleteField(),
        customer_address: deleteField(),
        total_amount: deleteField(),
        item_count: deleteField(),
        start_time: deleteField(),
        end_time: deleteField(),
        order_id: deleteField(),
        active_order_id: deleteField(),
        active_order_number: deleteField(),
        occupied_by: deleteField(),
        tab_open: deleteField(),
        tab_orders: deleteField(),
        tab_total: deleteField(),
        tab_item_count: deleteField(),
        tab_rounds: deleteField(),
        updated_at: new Date().toISOString(),
        updatedAt: serverTimestamp()
      });

      console.log('✅ [FIREBASE TABLE] Table reset successfully');
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to reset table:', error);
      throw error;
    }
  }

  /**
   * Initialize default tables if none exist
   */
  public async initializeDefaultTables(): Promise<void> {
    try {
      console.log('🪑 [FIREBASE TABLE] Checking if tables need initialization...');
      
      const existingTables = await this.getTables();
      
      if (existingTables.length === 0) {
        console.log('🪑 [FIREBASE TABLE] No tables found, creating default tables...');
        
        // Create default indoor tables (1-9)
        for (let i = 1; i <= 9; i++) {
          await this.createTable({
            number: i,
            status: 'available',
            capacity: 4,
            area: 'indoor'
          });
        }
        
        // Create default outdoor tables (10-29)
        for (let i = 10; i <= 29; i++) {
          await this.createTable({
            number: i,
            status: 'available',
            capacity: 4,
            area: 'outdoor'
          });
        }
        
        console.log('✅ [FIREBASE TABLE] Default tables created successfully');
      } else {
        console.log(`✅ [FIREBASE TABLE] Found ${existingTables.length} existing tables`);
      }
    } catch (error) {
      console.error('❌ [FIREBASE TABLE] Failed to initialize default tables:', error);
      throw error;
    }
  }
}

export const firebaseTableService = FirebaseTableService.getInstance();
